import { resolveProjectSettings } from "@afar-research/afar";
import type { EnvironmentId, ServerSettings } from "@afar-research/contracts";
import { DEFAULT_SERVER_SETTINGS } from "@afar-research/contracts";
import { createContext, use, useMemo, type ReactNode } from "react";

import { selectProjectGroupingSettings } from "../../logicalProject";
import { buildSidebarProjectSnapshots } from "../../sidebarProjectGrouping";
import { useClientSettings } from "../../hooks/useSettings";
import { useProjects, useServerConfigs } from "../../state/entities";
import { useEnvironments, usePrimaryEnvironmentId } from "../../state/environments";
import { resolveSettingsScope, type SettingsScopeSearch } from "./settingsScope";

export interface SettingsScopeContextValue {
  readonly scope: ReturnType<typeof resolveSettingsScope>;
  readonly search: SettingsScopeSearch;
  readonly selectScope: (next: SettingsScopeSearch) => void;
  /** Current settings for every Environment the scope can write to. */
  readonly settingsByEnvironment: ReadonlyMap<EnvironmentId, ServerSettings>;
  /**
   * The settings a row displays. At a project scope this is one representative
   * checkout's effective view; the write still fans out to all of them.
   */
  readonly displaySettings: ServerSettings;
}

const SettingsScopeContext = createContext<SettingsScopeContextValue | null>(null);

export function SettingsScopeProvider(props: {
  readonly search: SettingsScopeSearch;
  readonly onChange: (next: SettingsScopeSearch) => void;
  readonly children: ReactNode;
}) {
  const projects = useProjects();
  const { environments } = useEnvironments();
  const groupingSettings = useClientSettings(selectProjectGroupingSettings);
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const groups = useMemo(
    () =>
      buildSidebarProjectSnapshots({
        projects,
        settings: groupingSettings,
        resolveEnvironmentLabel: (environmentId) =>
          environments.find((candidate) => candidate.environmentId === environmentId)?.label ??
          null,
        primaryEnvironmentId,
      }),
    [environments, groupingSettings, primaryEnvironmentId, projects],
  );
  const scope = useMemo(
    () =>
      resolveSettingsScope(
        props.search,
        groups,
        environments.map((environment) => ({
          environmentId: environment.environmentId,
          label: environment.label,
        })),
      ),
    [environments, groups, props.search],
  );
  // One subscription for every Environment's config, then narrowed to the
  // scope: a per-Environment subscription would change hook count with the
  // selection.
  const serverConfigs = useServerConfigs();
  const settingsByEnvironment = useMemo(
    () =>
      new Map(
        scope.environmentIds.map((environmentId) => [
          environmentId,
          serverConfigs.get(environmentId)?.settings ?? DEFAULT_SERVER_SETTINGS,
        ]),
      ),
    [scope.environmentIds, serverConfigs],
  );
  const displaySettings = useMemo(() => {
    const representative = scope.members[0] ?? null;
    const environmentId = representative?.environmentId ?? scope.environmentIds[0] ?? null;
    const settings =
      environmentId === null
        ? DEFAULT_SERVER_SETTINGS
        : (settingsByEnvironment.get(environmentId) ?? DEFAULT_SERVER_SETTINGS);
    return resolveProjectSettings(settings, representative?.id ?? null);
  }, [scope.environmentIds, scope.members, settingsByEnvironment]);

  const value = useMemo(
    () => ({
      scope,
      search: props.search,
      selectScope: props.onChange,
      settingsByEnvironment,
      displaySettings,
    }),
    [displaySettings, props.onChange, props.search, scope, settingsByEnvironment],
  );
  return <SettingsScopeContext value={value}>{props.children}</SettingsScopeContext>;
}

/** Null outside the settings layout — rows also render in dialogs and cards. */
export function useOptionalSettingsScope(): SettingsScopeContextValue | null {
  return use(SettingsScopeContext);
}

export function useSettingsScope(): SettingsScopeContextValue {
  const scope = useOptionalSettingsScope();
  if (scope === null) {
    throw new Error("Settings scope must be read inside SettingsScopeProvider.");
  }
  return scope;
}
